import { api } from '@/api/client'
import { currentDemoUserId } from '@/api/demoAuth'
import { endpoints } from '@/api/endpoints'
import { demoAdmin } from '@/features/admin/demo/demoAdmin'
import type { AdminScope, NodeRef, StaffMember } from '@/features/admin/types'
import { env } from '@/lib/env'

/**
 * `GET /api/staff` and block/unblock (spec §5.3) — new endpoints, not in the cascading-rights
 * plan. The wire row is the UI row; `toStaffMember` exists so a divergence has a home.
 */
interface StaffRowResponse {
  publicId: string
  displayName: string
  email: string
  status: string
  statusLabel: string
  lastLoginAt: string | null
  assignments: { role: { key: string; label: string }; node: NodeRef }[]
  canManage: boolean
}

interface StaffResponse {
  items: StaffRowResponse[]
}

export function toStaffMember(row: StaffRowResponse): StaffMember {
  return {
    ...row,
    assignments: row.assignments.map((assignment) => ({
      role: { ...assignment.role },
      node: { ...assignment.node },
    })),
  }
}

export const staffApi = {
  list: async (scope: AdminScope): Promise<StaffMember[]> => {
    if (env.demoMode) return demoAdmin.listStaff(scope)
    const response = await api.get<StaffResponse>(endpoints.staff.list, { params: scope })
    return response.items.map(toStaffMember)
  },

  block: async (id: string): Promise<StaffMember> => {
    // The demo has no server session to know "who is asking", so the signed-in id travels in.
    if (env.demoMode) return demoAdmin.block(id, currentDemoUserId())
    return toStaffMember(await api.post<StaffRowResponse>(endpoints.staff.block(id)))
  },

  unblock: async (id: string): Promise<StaffMember> => {
    if (env.demoMode) return demoAdmin.unblock(id)
    return toStaffMember(await api.post<StaffRowResponse>(endpoints.staff.unblock(id)))
  },
}
